import React, { useMemo, useState, useEffect } from "react";
import { useRef } from "react";
import { appendProductionInstanceScope } from "../productionRequestScope";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Form,
  InputGroup,
  FormControl,
  Tabs,
  Tab,
  Table,
  ProgressBar,
  Modal,
  Offcanvas,
  ButtonGroup,
} from "react-bootstrap";
import {
  Search,
  Plus,
  Filter,
  Calendar,
  Factory,
  Users,
  Gauge,
  Activity,
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  Wrench,
  Play,
  Pause,
  Download,
  SortAsc,
  SortDesc,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
} from "recharts";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useParams } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";

import "./EliteProduction.css";
const fetch = (input, init = {}) => window.fetch(appendProductionInstanceScope(input, {
  pathname: window.location.pathname,
  apiBase: process.env.REACT_APP_NODEJS_SERVER_URL,
}), {
  ...init,
  headers: { accessToken: localStorage.getItem("accessToken") || "", ...(init.headers || {}) },
});

/**
 * Elite — Machine Management UI (V2) — React 17 + React-Bootstrap
 * Uses ONLY packages in your package.json: react, react-bootstrap, lucide-react, recharts, @dnd-kit/*
 *
 * What's new in V2:
 * 1) Quick status chips (All/Running/Idle/Down/Maint) with counts
 * 2) Table sorting + bulk select + CSV export
 * 3) Drag & drop job queue in the details drawer (dnd-kit)
 * 4) Service due/overdue badges
 * 5) Power switch + basic form validation in Add Machine
 */

// -------------------- Mock Data --------------------
const DEPARTMENTS = [
  { id: "ALL", name: "All" },
  { id: "EMB", name: "Embroidery" },
  { id: "SCR", name: "Screening" },
  { id: "ENG", name: "Engraving" },
  { id: "CRS", name: "Cresting" },
  { id: "DTF", name: "Direct To Film" }, // new department
  { id: "AWD", name: "Awards" }, // new department
];

const OPERATORS = [
  { id: "op-amy", name: "Amy" },
  { id: "op-john", name: "John" },
  { id: "op-luis", name: "Luis" },
  { id: "op-ria", name: "Ria" },
  { id: "op-zoe", name: "Zoe" },
];

const STATUS = ["Running", "Idle", "Down", "Maintenance"];

const MOCK_MACHINES = [
  {
    id: "EMB-01",
    name: "Tajima 12-Head",
    department: "EMB",
    type: "Embroidery",
    heads: 12,
    capacityPerHour: 220,
    status: "Running",
    operatorId: "op-amy",
    utilization: 86,
    lastService: "2025-07-20",
    nextService: "2025-08-25",
    powerOn: true,
    queue: [
      { id: "PO-10021", job: "Hoodies – Crest A", qty: 180, due: "2025-08-16" },
      { id: "PO-10028", job: "Tees – Left Chest", qty: 240, due: "2025-08-18" },
    ],
  },
  {
    id: "EMB-02",
    name: "Barudan 8-Head",
    department: "EMB",
    type: "Embroidery",
    heads: 8,
    capacityPerHour: 150,
    status: "Idle",
    operatorId: "op-john",
    utilization: 42,
    lastService: "2025-06-30",
    nextService: "2025-08-10",
    powerOn: false,
    queue: [
      { id: "PO-10035", job: "Caps – Monogram", qty: 120, due: "2025-08-17" },
    ],
  },
  {
    id: "SCR-01",
    name: "M&R 6-Color Press",
    department: "SCR",
    type: "Screening",
    heads: 6,
    capacityPerHour: 320,
    status: "Running",
    operatorId: "op-luis",
    utilization: 78,
    lastService: "2025-07-12",
    nextService: "2025-09-05",
    powerOn: true,
    queue: [
      { id: "PO-10011", job: "Posters – Batch 3", qty: 400, due: "2025-08-14" },
    ],
  },
  {
    id: "ENG-01",
    name: "Gravotech LS100",
    department: "ENG",
    type: "Engraving",
    heads: 1,
    capacityPerHour: 60,
    status: "Maintenance",
    operatorId: "op-ria",
    utilization: 0,
    lastService: "2025-08-12",
    nextService: "2025-08-12",
    powerOn: false,
    queue: [],
  },
  {
    id: "CRS-01",
    name: "Heat Press 15x15",
    department: "CRS",
    type: "Cresting",
    heads: 1,
    capacityPerHour: 90,
    status: "Down",
    operatorId: "op-zoe",
    utilization: 0,
    lastService: "2025-07-05",
    nextService: "2025-08-20",
    powerOn: false,
    queue: [
      {
        id: "PO-10040",
        job: "Jackets – Back Crest",
        qty: 60,
        due: "2025-08-19",
      },
    ],
  },
];

const DEPARTMENT_MAP = {
  EMB: "Embroidery",
  SCR: "Screening",
  ENG: "Engraving",
  CRS: "Cresting",
};

// -------------------- Utils --------------------
// function buildUtilizationSeries(machineId) {
//   const seed = machineId.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
//   const days = Array.from({ length: 14 }).map((_, i) => {
//     const pct = Math.abs(Math.sin((seed + i) * 12.9898) * 100) % 100;
//     return { day: `D${i + 1}`, utilization: Math.round(pct) };
//   });
//   return days;
// }

function buildUtilizationSeries(machineId) {
  const strId = machineId.toString(); // ensure string
  const seed = strId.split("").reduce((a, c) => a + c.charCodeAt(0), 0);

  const days = Array.from({ length: 14 }).map((_, i) => {
    const pct = Math.abs(Math.sin((seed + i) * 12.9898) * 100) % 100;
    return { day: `D${i + 1}`, utilization: Math.round(pct) };
  });

  return days;
}

function statusVariant(s) {
  switch (s) {
    case "Running":
      return "success";
    case "Idle":
      return "warning";
    case "Down":
      return "danger";
    case "Maintenance":
      return "info";
    default:
      return "secondary";
  }
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  const today = new Date();
  const diff = Math.floor(
    (d - new Date(today.getFullYear(), today.getMonth(), today.getDate())) /
      86400000,
  );
  return diff; // negative => overdue; positive => days remaining
}

function serviceBadge(nextService) {
  const du = daysUntil(nextService);
  if (du == null) return null;
  if (du < 0)
    return (
      <Badge bg="danger" className="ms-2">
        Overdue
      </Badge>
    );
  if (du <= 7)
    return (
      <Badge bg="warning" className="ms-2">
        Due in {du}d
      </Badge>
    );
  return null;
}

function Kpi({ icon: Icon, label, value, hint }) {
  return (
    <Card className="shadow-sm h-100">
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="text-muted small">{label}</div>
          <Icon size={16} className="text-muted" />
        </div>
        <div className="fw-semibold" style={{ fontSize: 22 }}>
          {value}
        </div>
        {hint && <div className="text-muted small mt-1">{hint}</div>}
      </Card.Body>
    </Card>
  );
}

function UtilizationSpark({ data }) {
  return (
    <div style={{ height: 64 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ top: 6, right: 8, left: 0, bottom: 0 }}
        >
          <XAxis dataKey="day" hide />
          <YAxis domain={[0, 100]} hide />
          <RTooltip
            formatter={(v) => `${v}%`}
            labelFormatter={() => "Utilization"}
          />
          <Line type="monotone" dataKey="utilization" dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---- DND Queue Row ----
function SortableQueueRow({ item, index }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    background: isDragging ? "#f8f9fa" : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="d-flex align-items-center justify-content-between border rounded p-2 mb-2"
      {...attributes}
      {...listeners}
    >
      <div className="small">
        <div className="fw-semibold">{item.job}</div>
        <div className="text-muted">
          {item.id} • Qty {item.qty} • Due {item.due}
        </div>
      </div>
      <div className="text-muted small">Drag</div>
    </div>
  );
}

function QueueRowFallback({ item, onMove, index, total }) {
  return (
    <div className="d-flex align-items-center justify-content-between border rounded p-2">
      <div className="small">
        <div className="fw-semibold">{item.job}</div>
        <div className="text-muted">
          {item.id} • Qty {item.qty} • Due {item.due}
        </div>
      </div>
      <div className="d-flex gap-1">
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={index === 0}
          onClick={(e) => {
            e.stopPropagation();
            onMove(index, index - 1);
          }}
        >
          <ChevronUp size={16} />
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={index === total - 1}
          onClick={(e) => {
            e.stopPropagation();
            onMove(index, index + 1);
          }}
        >
          <ChevronDown size={16} />
        </Button>
      </div>
    </div>
  );
}

export default function EliteMachinesTab({ department }) {
  const [activeDept, setActiveDept] = useState("ALL");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [operatorFilter, setOperatorFilter] = useState("ALL");
  const [view, setView] = useState("cards");
  const [machines, setMachines] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [tableSelection, setTableSelection] = useState([]); // array of ids
  const [sortBy, setSortBy] = useState("id");
  const [sortDir, setSortDir] = useState("asc");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const ROUTE_TO_DEPT = {
    embroidery: "EMB",
    screening: "SCR",
    engraving: "ENG",
    cresting: "CRS",
    "direct-to-film": "DTF", // matches URL slug
    "direct to film": "DTF",
    awards: "AWD", // matches URL slug
  };

  const [users, setUsers] = useState([]);

  // const filtered = useMemo(() => {
  //   return machines.filter((m) => {
  //     // if (activeDept !== "ALL" && m.department !== activeDept) return false;
  //     if (activeDept !== "ALL" && m.department !== DEPARTMENT_MAP[activeDept])
  //       return false;

  //     if (statusFilter !== "ALL" && m.status !== statusFilter) return false;
  //     if (operatorFilter !== "ALL" && m.operatorId !== operatorFilter)
  //       return false;
  //     if (search) {
  //       const hay = `${m.id} ${m.name} ${m.type}`.toLowerCase();
  //       if (!hay.includes(search.toLowerCase())) return false;
  //     }
  //     return true;
  //   });
  // }, [machines, activeDept, statusFilter, operatorFilter, search]);

  // const filtered = useMemo(() => {
  //   return machines.filter((m) => {
  //     // map machine.department (like "Embroidery") to code
  //     const machineDeptCode =
  //       ROUTE_TO_DEPT[m.department?.toLowerCase()] || "ALL";

  //     if (activeDept !== "ALL" && machineDeptCode !== activeDept) return false;

  //     if (statusFilter !== "ALL" && m.status !== statusFilter) return false;
  //     if (operatorFilter !== "ALL" && m.operatorId !== operatorFilter)
  //       return false;

  //     if (search) {
  //       const hay = `${m.id} ${m.name} ${m.type}`.toLowerCase();
  //       if (!hay.includes(search.toLowerCase())) return false;
  //     }
  //     return true;
  //   });
  // }, [machines, activeDept, statusFilter, operatorFilter, search]);

  const filtered = useMemo(() => {
    return machines.filter((m) => {
      // map machine.department (like "Embroidery") to code
      const machineDeptCode =
        ROUTE_TO_DEPT[m.department?.toLowerCase()] || "ALL";

      if (activeDept !== "ALL" && machineDeptCode !== activeDept) return false;
      if (statusFilter !== "ALL" && m.status !== statusFilter) return false;

      // Updated operator filter - handles both string and number comparison
      if (operatorFilter !== "ALL" && m.operatorId != operatorFilter)
        return false;

      if (search) {
        const hay = `${m.id} ${m.name} ${m.type}`.toLowerCase();
        if (!hay.includes(search.toLowerCase())) return false;
      }
      return true;
    });
  }, [machines, activeDept, statusFilter, operatorFilter, search]);

  console.log("Machines:", machines);

  // const sorted = useMemo(() => {
  //   const arr = [...filtered];
  //   arr.sort((a, b) => {
  //     const dir = sortDir === "asc" ? 1 : -1;
  //     const val = (key, obj) => {
  //       if (key === "operator")
  //         return (
  //           OPERATORS.find((o) => o.id === obj.operatorId)?.name || ""
  //         ).toLowerCase();
  //       return obj[key] ?? "";
  //     };
  //     const va = val(sortBy, a);
  //     const vb = val(sortBy, b);
  //     if (typeof va === "number" && typeof vb === "number")
  //       return (va - vb) * dir;
  //     return String(va).localeCompare(String(vb)) * dir;
  //   });
  //   return arr;
  // }, [filtered, sortBy, sortDir]);

  // const sorted = useMemo(() => {
  //   const arr = [...filtered];
  //   arr.sort((a, b) => {
  //     const dir = sortDir === "asc" ? 1 : -1;
  //     const val = (key, obj) => {
  //       if (key === "operator") {
  //         // Updated to use API users instead of OPERATORS
  //         const operator = users.find((u) => u.id == obj.operatorId);
  //         return operator
  //           ? `${operator.first_name} ${operator.last_name}`.toLowerCase()
  //           : "";
  //       }
  //       return obj[key] ?? "";
  //     };
  //     const va = val(sortBy, a);
  //     const vb = val(sortBy, b);
  //     if (typeof va === "number" && typeof vb === "number")
  //       return (va - vb) * dir;
  //     return String(va).localeCompare(String(vb)) * dir;
  //   });
  //   return arr;
  // }, [filtered, sortBy, sortDir, users]); // Added users dependency

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const val = (key, obj) => {
        if (key === "operator") {
          const operator = users.find((u) => u.id == obj.operatorId);
          return operator ? operator.name.toLowerCase() : ""; // Changed
        }
        return obj[key] ?? "";
      };
      const va = val(sortBy, a);
      const vb = val(sortBy, b);
      if (typeof va === "number" && typeof vb === "number")
        return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
    return arr;
  }, [filtered, sortBy, sortDir, users]);

  // const kpis = useMemo(() => {
  //   const total = machines.length;
  //   const running = machines.filter((m) => m.status === "Running").length;
  //   const idle = machines.filter((m) => m.status === "Idle").length;
  //   const down = machines.filter((m) => m.status === "Down").length;
  //   const maintenance = machines.filter((m) => m.status === "Maintenance")
  //     .length;
  //   const avgUtil = Math.round(
  //     machines.reduce((a, m) => a + (m.utilization || 0), 0) /
  //       Math.max(1, total)
  //   );
  //   const capacity = machines.reduce((a, m) => a + (m.capacityPerHour || 0), 0);
  //   const dueSoon = machines.filter((m) => {
  //     const du = daysUntil(m.nextService);
  //     return du != null && du <= 7;
  //   }).length;
  //   const overdue = machines.filter((m) => {
  //     const du = daysUntil(m.nextService);
  //     return du != null && du < 0;
  //   }).length;
  //   return {
  //     total,
  //     running,
  //     idle,
  //     down,
  //     maintenance,
  //     avgUtil,
  //     capacity,
  //     dueSoon,
  //     overdue,
  //   };
  // }, [machines]);

  const kpis = useMemo(() => {
    // Use FILTERED machines instead of all machines
    const departmentMachines = filtered; // This already filters by activeDept

    const total = departmentMachines.length;
    const running = departmentMachines.filter((m) => m.status === "Running")
      .length;
    const idle = departmentMachines.filter((m) => m.status === "Idle").length;
    const down = departmentMachines.filter((m) => m.status === "Down").length;
    const maintenance = departmentMachines.filter(
      (m) => m.status === "Maintenance",
    ).length;

    const avgUtil = Math.round(
      departmentMachines.reduce((a, m) => a + m.utilization, 0) /
        Math.max(1, total),
    );

    const capacity = departmentMachines.reduce(
      (a, m) => a + m.capacityPerHour,
      0,
    );

    const dueSoon = departmentMachines.filter((m) => {
      const du = daysUntil(m.nextService);
      return du !== null && du <= 7;
    }).length;

    const overdue = departmentMachines.filter((m) => {
      const du = daysUntil(m.nextService);
      return du !== null && du < 0;
    }).length;

    return {
      total,
      running,
      idle,
      down,
      maintenance,
      avgUtil,
      capacity,
      dueSoon,
      overdue,
    };
  }, [filtered]); // Changed dependency from [machines] to [filtered]

  function openMachine(m) {
    setSelected(m);
    setShowDetail(true);
  }

  // function updateSelected(patch) {
  //   if (!selected) return;
  //   setMachines((prev) =>
  //     prev.map((m) => (m.id === selected.id ? { ...m, ...patch } : m))
  //   );
  //   setSelected((s) => ({ ...s, ...patch }));
  // }

  async function updateSelected(patch) {
    if (!selected) return;

    try {
      // Call your PATCH API first
      const updated = await patchMachine(selected.id, patch);

      // Update local state with the response
      setMachines((prev) =>
        prev.map((m) => (m.id === selected.id ? { ...m, ...patch } : m)),
      );
      setSelected((s) => ({ ...s, ...patch }));

      console.log("Machine updated successfully:", updated);
    } catch (err) {
      console.error("Failed to update machine:", err);
      alert("Failed to update machine: " + err.message);
      // Optionally revert UI state on error
    }
  }

  function moveQueue(from, to) {
    if (!selected) return;
    const q = [...selected.queue];
    const [it] = q.splice(from, 1);
    q.splice(to, 0, it);
    updateSelected({ queue: q });
  }

  // ---- Add Machine (validation) ----
  const [newMachine, setNewMachine] = useState({
    code: "",
    name: "",
    department_id: "",
    department: "EMB",
    type: "Embroidery",
    heads: 1,
    capacityPerHour: 60,
    hourlyRate: 0,
    hourlyRateCurrency: "CAD",
    status: "Idle",
    operatorId: "",
    utilization: 0,
    lastService: "2025-08-10",
    nextService: "2025-09-10",
    powerOn: false,
    queue: [],
  });
  const [touched, setTouched] = useState({ id: false, name: false });

  // function addMachine() {
  //   setTouched({ code: true, name: true });
  //   if (!newMachine.code || !newMachine.name || !newMachine.department_id)
  //     return;
  //   setMachines((prev) => [...prev, newMachine]);
  //   setShowAdd(false);
  //   setNewMachine({
  //     code: "",
  //     name: "",
  //     department_id: "",
  //     department: "EMB",
  //     type: "Embroidery",
  //     heads: 1,
  //     capacityPerHour: 60,
  //     status: "Idle",
  //     operatorId: "",
  //     utilization: 0,
  //     lastService: "2025-08-10",
  //     nextService: "2025-09-10",
  //     powerOn: false,
  //     queue: [],
  //   });
  //   setTouched({ id: false, name: false });
  // }

  async function addMachine() {
    setTouched({ code: true, name: true });

    if (!newMachine.code || !newMachine.name || !newMachine.department_id) {
      // alert("Please fill in all required fields");
      return;
    }

    try {
      const response = await fetch(
        // `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/machines/`,
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/machines/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(newMachine),
        },
      );

      const data = await response.json();

      if (response.ok && data.success) {
        // setMachines((prev) => [...prev, data.machine]);
        setMachines((prev) => [...prev, data.data]); // <-- correct key
        setShowAdd(false);
        setNewMachine({
          code: "",
          name: "",
          department_id: departments[0]?.id || "",
          type: "",
          heads: 1,
          capacityPerHour: 60,
          hourlyRate: 0,
          hourlyRateCurrency: "CAD",
          status: "Idle",
          operatorId: "",
          utilization: 0,
          lastService: "2025-08-10",
          nextService: "2025-09-10",
          powerOn: false,
          queue: [],
        });
        setTouched({ code: false, name: false });
      } else {
        alert(data.message || "Failed to add machine");
      }
    } catch (err) {
      console.error("Error adding machine:", err);
      alert("Something went wrong while adding machine");
    }
  }

  useEffect(() => {
    async function loadMachines() {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/machines/`,
        );
        const data = await res.json();

        if (res.ok && data.success) {
          // Map API response to your UI shape
          const normalized = data.data.map((m) => ({
            id: m.id, // numeric
            code: m.code, // keep both
            name: m.name,
            department: m.department,
            type: m.type || "",
            heads: m.heads ?? 0,
            capacityPerHour: m.capacityPerHour ?? 0,
            hourlyRate: m.hourlyRate ?? 0,
            hourlyRateCurrency: m.hourlyRateCurrency ?? "CAD",
            status: m.status,
            utilization: m.utilization ?? 0,
            operatorId: m.operatorId || "",
            powerOn: m.powerOn ?? false,
            lastService: m.lastService,
            nextService: m.nextService,
            notes: m.notes,
            queue: m.queue || [], // backend doesn’t send? fallback empty
          }));
          setMachines(normalized);
        } else {
          console.error("Failed to load machines:", data.message);
        }
      } catch (err) {
        console.error("Error fetching machines:", err);
      }
    }

    loadMachines();
  }, []);

  async function patchMachine(id, updates) {
    try {
      const res = await fetch(
        // `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/machines/${id}`,
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/machines/${id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updates),
        },
      );

      if (!res.ok) throw new Error("Failed to update machine");

      const data = await res.json();
      return data.data; // assuming backend responds with { success, data: { ... } }
    } catch (err) {
      console.error("PATCH error:", err);
      throw err;
    }
  }

  // ---- Bulk Select & CSV ----
  const allChecked =
    tableSelection.length > 0 && tableSelection.length === sorted.length;
  const someChecked =
    tableSelection.length > 0 && tableSelection.length < sorted.length;
  function toggleAll() {
    setTableSelection((sel) =>
      sel.length === sorted.length ? [] : sorted.map((m) => m.id),
    );
  }
  function toggleOne(id) {
    setTableSelection((sel) =>
      sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id],
    );
  }
  // function exportCSV(rows) {
  //   const header = [
  //     "id",
  //     "name",
  //     "department",
  //     "type",
  //     "heads",
  //     "capacityPerHour",
  //     "status",
  //     "operator",
  //     "utilization",
  //     "lastService",
  //     "nextService",
  //     "powerOn",
  //   ];
  //   const lines = [header.join(",")];
  //   rows.forEach((m) => {
  //     // const op = OPERATORS.find((o) => o.id === m.operatorId)?.name || "";
  //     const operator = users.find((u) => u.id == m.operatorId);
  //     const op = operator ? `${operator.first_name} ${operator.last_name}` : "";
  //     const vals = [
  //       m.id,
  //       m.name,
  //       m.department,
  //       m.type,
  //       m.heads,
  //       m.capacityPerHour,
  //       m.status,
  //       op,
  //       m.utilization,
  //       m.lastService,
  //       m.nextService,
  //       m.powerOn,
  //     ].map((v) => (typeof v === "string" ? `"${v.replace(/"/g, '""')}"` : v));
  //     lines.push(vals.join(","));
  //   });
  //   const blob = new Blob([lines.join("")], {
  //     type: "text/csv;charset=utf-8;",
  //   });
  //   const url = URL.createObjectURL(blob);
  //   const a = document.createElement("a");
  //   a.href = url;
  //   a.download = "machines.csv";
  //   a.click();
  //   URL.revokeObjectURL(url);
  // }

  function exportCSV(rows) {
    const header = [
      "id",
      "name",
      "department",
      "type",
      "heads",
      "capacityPerHour",
      "status",
      "operator",
      "hourlyRate",
      "hourlyRateCurrency",
      "utilization",
      "lastService",
      "nextService",
      "powerOn",
    ];
    const lines = [header.join(",")];
    rows.forEach((m) => {
      const operator = users.find((u) => u.id == m.operatorId);
      const op = operator ? operator.name : ""; // Changed
      const vals = [
        m.id,
        m.name,
        m.department,
        m.type,
        m.heads,
        m.capacityPerHour,
        m.status,
        op,
        m.hourlyRate ?? 0,
        m.hourlyRateCurrency || "CAD",
        m.utilization,
        m.lastService,
        m.nextService,
        m.powerOn,
      ].map((v) => (typeof v === "string" ? `"${v.replace(/"/g, '""')}"` : v));
      lines.push(vals.join(","));
    });
    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "machines.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  function headerSortButton(key, label) {
    const isActive = sortBy === key;
    const Icon = isActive ? (sortDir === "asc" ? SortAsc : SortDesc) : null;
    return (
      <Button
        variant="link"
        className="p-0 text-decoration-none"
        onClick={() => {
          if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
          else {
            setSortBy(key);
            setSortDir("asc");
          }
        }}
      >
        {label} {Icon && <Icon size={14} />}
      </Button>
    );
  }

  // Quick status chips
  function StatusChip({ label, value }) {
    const active = statusFilter === value;
    const variant =
      value === "Running"
        ? "success"
        : value === "Idle"
        ? "warning"
        : value === "Down"
        ? "danger"
        : value === "Maintenance"
        ? "info"
        : "secondary";
    const count =
      value === "ALL"
        ? kpis.total
        : machines.filter((m) => m.status === value).length;
    return (
      <Button
        size="sm"
        variant={active ? variant : "outline-" + variant}
        onClick={() => setStatusFilter(active ? "ALL" : value)}
      >
        {label}{" "}
        <Badge bg={active ? "light" : "secondary"} className="ms-1">
          {count}
        </Badge>
      </Button>
    );
  }

  const [showEdit, setShowEdit] = useState(false);
  const [editMachine, setEditMachine] = useState(null);

  useEffect(() => {
    if (selected) setEditMachine(selected); // preload when machine selected
  }, [selected]);

  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);

  const dummyAuditLogs = [
    {
      date: "2025-08-01T10:00:00Z",
      user: "Admin",
      action: "Created",
      details: "Machine created with capacity 100/hr",
    },
    {
      date: "2025-08-05T14:30:00Z",
      user: "Manager",
      action: "Updated",
      details: "Changed status from Active → Inactive",
    },
    {
      date: "2025-08-10T09:15:00Z",
      user: "Operator",
      action: "Updated",
      details: "Capacity updated from 100/hr → 120/hr",
    },
    {
      date: "2025-08-12T16:00:00Z",
      user: "Supervisor",
      action: "Commented",
      details: "Routine maintenance scheduled",
    },
  ];

  const openAuditLog = () => {
    setAuditLogs(dummyAuditLogs);
    setShowAuditLog(true);
  };

  const [departments, setDepartments] = useState([]);

  // useEffect(() => {
  //   fetch("http://localhost:3001/production/departments?active=true",
  //     { method: "GET",
  //       headers: {
  //         "Content-Type": "application/json",
  //       },
  //     }
  //   )
  //     .then((res) => res.json())
  //     .then((data) => {
  //       if (data.success) setDepartments(data.data);
  //     })
  //     .catch((err) => console.error("Error fetching departments:", err));
  // }, []);


  useEffect(() => {
  async function loadDepartments() {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/departments?active=true`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            accessToken: localStorage.getItem("accessToken"),
          },
        }
      );

      const data = await res.json();

      if (data.success) {
        setDepartments(data.data);
      } else {
        console.error("Failed to load departments:", data.message);
      }
    } catch (err) {
      console.error("Error fetching departments:", err);
    }
  }

  loadDepartments();
}, []);

  const { departmentSlug } = useParams(); // embroidery, screening, engraving

  // useEffect(() => {
  //   if (departmentSlug && ROUTE_TO_DEPT[departmentSlug]) {
  //     setActiveDept(ROUTE_TO_DEPT[departmentSlug]);
  //   } else {
  //     setActiveDept("ALL");
  //   }
  // }, [departmentSlug]);

  // set activeDept based on prop "department"
  useEffect(() => {
    if (department && ROUTE_TO_DEPT[department]) {
      setActiveDept(ROUTE_TO_DEPT[department]); // specific dept
    } else {
      setActiveDept("ALL"); // default (Production root)
    }
  }, [department]);

  useEffect(() => {
    if (departments.length > 0 && departmentSlug) {
      const dept = departments.find(
        (d) => d.name.toLowerCase() === departmentSlug.toLowerCase(),
      );
      if (dept) {
        setNewMachine((prev) => ({
          ...prev,
          department_id: dept.id,
          type: dept.name,
        }));
      }
    }
  }, [departments, departmentSlug]);

  const [isUpdating, setIsUpdating] = useState(false);

  async function updateSelected(patch) {
    if (!selected || isUpdating) return;

    setIsUpdating(true);
    try {
      const updated = await patchMachine(selected.id, patch);
      setMachines((prev) =>
        prev.map((m) => (m.id === selected.id ? { ...m, ...patch } : m)),
      );
      setSelected((s) => ({ ...s, ...patch }));
      console.log("Machine updated successfully:", updated);
    } catch (err) {
      console.error("Failed to update machine:", err);
      alert("Failed to update machine: " + err.message);
    } finally {
      setIsUpdating(false);
    }
  }

  // const [users, setUsers] = useState([]);
  // const [userSearch, setUserSearch] = useState("");

  // // Fetch users from API
  // useEffect(() => {
  //   async function loadUsers() {
  //     try {
  //       const res = await fetch(
  //         "http://localhost:3001/auth/byinstanceUniqueId/elite-promo-marketing"
  //       );
  //       const data = await res.json();
  //       if (data.listOfUsers) {
  //         setUsers(data.listOfUsers);
  //       }
  //     } catch (err) {
  //       console.error("Error fetching users:", err);
  //     }
  //   }
  //   loadUsers();
  // }, []);

  // // Filter users based on search
  // const filteredUsers = useMemo(() => {
  //   if (!userSearch) return users;
  //   return users.filter((user) => {
  //     const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
  //     const email = user.email?.toLowerCase() || "";
  //     const searchTerm = userSearch.toLowerCase();
  //     return fullName.includes(searchTerm) || email.includes(searchTerm);
  //   });
  // }, [users, userSearch]);

  // Operator Dropdown States
  const [operatorSearchQuery, setOperatorSearchQuery] = useState("");
  const [showOperatorDropdown, setShowOperatorDropdown] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const operatorDropdownRef = useRef(null);

  // Users state

  // Filter users based on search
  // const filteredUsers = useMemo(() => {
  //   if (!operatorSearchQuery) return users;
  //   return users.filter((user) => {
  //     const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
  //     const email = user.email?.toLowerCase() || "";
  //     const role = user.role?.toLowerCase() || "";
  //     const searchTerm = operatorSearchQuery.toLowerCase();
  //     return (
  //       fullName.includes(searchTerm) ||
  //       email.includes(searchTerm) ||
  //       role.includes(searchTerm)
  //     );
  //   });
  // }, [users, operatorSearchQuery]);

  const filteredUsers = useMemo(() => {
    if (!operatorSearchQuery) return users;
    return users.filter((user) => {
      // NEW API structure uses 'name' field instead of separate first/last names
      const fullName = (user.name || "").toLowerCase();
      const searchTerm = operatorSearchQuery.toLowerCase();
      return fullName.includes(searchTerm);
    });
  }, [users, operatorSearchQuery]);

  // Operator Input Change
  const handleOperatorInputChange = (e) => {
    setOperatorSearchQuery(e.target.value);
    setShowOperatorDropdown(true);
  };

  // // Operator Select
  // const handleOperatorSelect = (userId, firstName, lastName) => {
  //   updateSelected({ operatorId: userId });
  //   setOperatorSearchQuery(`${firstName} ${lastName}`);
  //   setShowOperatorDropdown(false);
  // };

  // // Operator Clear
  // const handleOperatorClear = () => {
  //   setOperatorSearchQuery("");
  //   setShowOperatorDropdown(false);
  //   updateSelected({ operatorId: null });
  // };

  // Fetch users from API
  // useEffect(() => {
  //   async function loadUsers() {
  //     setUsersLoading(true);
  //     try {
  //       const res = await fetch(
  //         "http://localhost:3001/auth/byinstanceUniqueId/elite-promo-marketing"
  //       );
  //       const data = await res.json();
  //       if (data.listOfUsers) {
  //         setUsers(data.listOfUsers);
  //       }
  //     } catch (err) {
  //       console.error("Error fetching users:", err);
  //     } finally {
  //       setUsersLoading(false);
  //     }
  //   }
  //   loadUsers();
  // }, []);

  // useEffect(() => {
  //   async function loadUsers() {
  //     setUsersLoading(true);
  //     try {
  //       const res = await fetch(
  //         "http://localhost:3001/auth/byinstanceUniqueId/elite-promo-marketing"
  //       );
  //       const data = await res.json();
  //       if (data.listOfUsers) {
  //         setUsers(data.listOfUsers);
  //       }
  //     } catch (err) {
  //       console.error("Error fetching users:", err);
  //     } finally {
  //       setUsersLoading(false);
  //     }
  //   }
  //   loadUsers();
  // }, []);

  useEffect(() => {
    async function loadUsers() {
      setUsersLoading(true);
      try {
        // NEW API - Use production-rep specific endpoint
        const res = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/users?role=production-rep`,
          {
            headers: {
              accessToken: localStorage.getItem("accessToken"),
              "Content-Type": "application/json",
            },
          },
        );

        const data = await res.json();

        if (data.success && data.users) {
          setUsers(data.users);
        } else {
          console.error("Failed to load users:", data.message);
        }
      } catch (err) {
        console.error("Error fetching users:", err);
      } finally {
        setUsersLoading(false);
      }
    }
    loadUsers();
  }, []);

  // Click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        operatorDropdownRef.current &&
        !operatorDropdownRef.current.contains(event.target)
      ) {
        setShowOperatorDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Set initial operator name when selected changes
  // useEffect(() => {
  //   if (selected?.operatorId) {
  //     const currentUser = users.find((u) => u.id === selected.operatorId);
  //     if (currentUser && !operatorSearchQuery) {
  //       setOperatorSearchQuery(
  //         `${currentUser.first_name} ${currentUser.last_name}`
  //       );
  //     }
  //   } else {
  //     setOperatorSearchQuery("");
  //   }
  // }, [selected?.operatorId, users]);

  // Set initial operator name when machine is selected
  useEffect(() => {
    if (selected?.operatorId && users.length > 0) {
      const currentUser = users.find((u) => u.id == selected.operatorId);
      if (currentUser) {
        setOperatorSearchQuery(
          `${currentUser.first_name} ${currentUser.last_name}`,
        );
      }
    } else {
      setOperatorSearchQuery("");
    }
  }, [selected?.id, selected?.operatorId, users]); // Added selected?.id as dependency

  // Add this new useEffect after your existing useEffects
  useEffect(() => {
    // Clear operator search when a different machine is selected
    setOperatorSearchQuery("");
    setShowOperatorDropdown(false);
  }, [selected?.id]); // Trigger when machine ID changes

  // Helper function to get display text for operator field
  const getOperatorDisplayText = () => {
    if (operatorSearchQuery) {
      return ""; // Show empty when searching
    }

    if (selected?.operatorId && selected?.operatorName) {
      return ""; // Will show selected operator name as placeholder
    }

    return "Select operator...";
  };

  // Enhanced operator select handler
  // const handleOperatorSelect = (operatorId, firstName, lastName) => {
  //   const operatorName = `${firstName} ${lastName}`;

  //   updateSelected({
  //     operatorId: operatorId,
  //     operatorName: operatorName,
  //   });

  //   setOperatorSearchQuery(""); // Clear search
  //   setShowOperatorDropdown(false); // Close dropdown
  // };

  const handleOperatorSelect = (operatorId, operatorName) => {
    updateSelected({
      operatorId: operatorId,
      operatorName: operatorName,
    });

    setOperatorSearchQuery("");
    setShowOperatorDropdown(false);
  };

  // Enhanced clear handler
  const handleOperatorClear = () => {
    setOperatorSearchQuery("");
    updateSelected({
      operatorId: null,
      operatorName: null,
    });
    setShowOperatorDropdown(false);
  };

  // Click outside handler enhancement
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        operatorDropdownRef.current &&
        !operatorDropdownRef.current.contains(event.target)
      ) {
        setShowOperatorDropdown(false);
        setOperatorSearchQuery(""); // Clear search when clicking outside
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  console.log("Payload:", editMachine);

  console.log("Active Department: ", activeDept);

  return (
    <Container fluid className="py-4">
      {/* Header */}
      {/* <Row className="align-items-center mb-3">
        <Col>
          <h4 className="mb-0">Machine Management</h4>
          <div className="text-muted small">
            Plan, monitor, and maintain all production machines across
            departments.
          </div>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => exportCSV(sorted)}>
            <Download size={16} className="me-1" /> Export CSV
          </Button>
          <Button variant="outline-secondary" onClick={() => setShowAdd(true)}>
            <Plus size={16} className="me-1" /> Add Machine
          </Button>
        </Col>
      </Row> */}
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        {/* Left Section - Title */}
        <div className="flex-grow-1" style={{ minWidth: "250px" }}>
          <h4
            className="mb-1 fw-semibold text-gray-900"
            style={{ fontSize: "24px", letterSpacing: "-0.02em" }}
          >
            Machine Management
          </h4>
          <p
            className="mb-0 text-muted"
            style={{ fontSize: "14px", lineHeight: "1.5" }}
          >
            Plan, monitor, and maintain all production machines across
            departments
          </p>
        </div>

        {/* Right Section - Actions */}
        <div className="d-flex gap-2 flex-shrink-0">
          <Button
            variant="outline-secondary"
            className="d-inline-flex align-items-center gap-2 px-3 py-2"
            style={{
              fontSize: "14px",
              fontWeight: "500",
              borderRadius: "6px",
              transition: "all 0.2s ease",
            }}
            onClick={() => exportCSV(sorted)}
          >
            <Download size={16} />
            <span>Export CSV</span>
          </Button>

          <Button
            variant="primary"
            className="d-inline-flex align-items-center gap-2 px-3 py-2"
            style={{
              fontSize: "14px",
              fontWeight: "500",
              borderRadius: "6px",
              transition: "all 0.2s ease",
            }}
            onClick={() => setShowAdd(true)}
          >
            <Plus size={16} />
            <span>Add Machine</span>
          </Button>
        </div>
      </div>
      {/* Filters */}
      <Card className="mb-3 mainTab">
        <Card.Body>
          <Row className="g-2 align-items-center">
            <Col lg="4">
              <Tabs
                activeKey={activeDept}
                onSelect={(k) => setActiveDept(k || "ALL")}
                id="dept-tabs"
              >
                {(department && ROUTE_TO_DEPT[department]
                  ? DEPARTMENTS.filter(
                      (d) => d.id === ROUTE_TO_DEPT[department],
                    ) // only that dept
                  : DEPARTMENTS
                ) // all depts if no department in route
                  .map((d) => (
                    <Tab eventKey={d.id} title={d.name} key={d.id} />
                  ))}
              </Tabs>
            </Col>
            <Col xs="auto">
              <InputGroup>
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>
                <FormControl
                  placeholder="Search machine, code, type…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs="auto" className="d-flex gap-1 flex-wrap">
              <StatusChip label="All" value="ALL" />
              <StatusChip label="Running" value="Running" />
              <StatusChip label="Idle" value="Idle" />
              <StatusChip label="Down" value="Down" />
              <StatusChip label="Maint" value="Maintenance" />
            </Col>

            <Col lg="auto">
              <Form.Select
                value={operatorFilter}
                onChange={(e) => setOperatorFilter(e.target.value)}
              >
                <option value="ALL">All Operators</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Button variant="outline-secondary">
                <Filter size={16} className="me-1" />
                More
              </Button>
            </Col>
            <Col xs="auto">
              <ButtonGroup>
                <Button
                  variant={view === "cards" ? "primary" : "outline-secondary"}
                  onClick={() => setView("cards")}
                >
                  Cards
                </Button>
                <Button
                  variant={view === "table" ? "primary" : "outline-secondary"}
                  onClick={() => setView("table")}
                >
                  Table
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      {/* KPIs */}
      <Row className="g-3 mb-3">
        <Col sm={6} lg={3}>
          <Kpi icon={Factory} label="Total Machines" value={kpis.total} />
        </Col>
        <Col sm={6} lg={3}>
          <Kpi
            icon={Activity}
            label="Running"
            value={kpis.running}
            hint={`${Math.round(
              (kpis.running / Math.max(1, kpis.total)) * 100,
            )}% online`}
          />
        </Col>
        <Col sm={6} lg={3}>
          <Kpi
            icon={AlertTriangle}
            label="Down / Maint"
            value={`${kpis.down} / ${kpis.maintenance}`}
          />
        </Col>
        <Col sm={6} lg={3}>
          <Kpi
            icon={Gauge}
            label="Avg Utilization"
            value={`${kpis.avgUtil}%`}
            hint={`Capacity/hr: ${kpis.capacity}`}
          />
        </Col>
      </Row>
      {/* Main */}
      {view === "cards" ? (
        <Row className="g-3">
          {sorted.map((m) => (
            <Col key={m.id} sm={6} xl={4}>
              <Card
                className="h-100 shadow-sm"
                role="button"
                onClick={() => openMachine(m)}
              >
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div className="d-flex align-items-center gap-2">
                      <Factory size={16} className="text-muted" />
                      <div className="fw-semibold">{m.name}</div>
                    </div>
                    <div>
                      <Badge bg={statusVariant(m.status)}>{m.status}</Badge>
                      {serviceBadge(m.nextService)}
                    </div>
                  </div>
                  {/* <div className="text-muted small mb-2">
                    {m.code} • {m.department} • {m.type || 'Not set'} • Heads: {m.heads}
                  </div> */}

                  {/*------------------------------------------------------------------------------------------------

                          conditional display
                
If user doesn't edit the type → Department: "Embroidery", Type: "Embroidery" → Shows only: EMB-01 - Embroidery - Heads: 12

If user edits the type → Department: "Embroidery", Type: "Custom Type" → Shows: EMB-01 - Embroidery - Custom Type - Heads: 12​ 

---------------------------------------------------------------------------------------------------*/}

                  <div className="text-muted small mb-2">
                    {m.code} • {m.department}
                    {m.type && m.type !== m.department && ` • ${m.type}`} •
                    Heads: {m.heads}
                  </div>

                  {/* <div className="d-flex align-items-center gap-2 small mb-1">
                    <Users size={16} className="text-muted" />
                    <span className="text-muted">Operator:</span>
                    <span className="fw-medium">
                      {OPERATORS.find((o) => o.id === m.operatorId)?.name ||
                        "Unassigned"}
                    </span>
                  </div> */}

                  <div className="d-flex align-items-center gap-2 small mb-1">
                    <Users size={16} className="text-muted" />
                    <span className="text-muted">Operator:</span>
                    {/* <span className="fw-medium">
                      {(() => {
                        const operator = users.find(
                          (u) => u.id === m.operatorId
                        );
                        return operator
                          ? `${operator.first_name} ${operator.last_name}`
                          : "Unassigned";
                      })()}
                    </span> */}

                    {/* <span className="fw-medium">
                      {(() => {
                        // Show loading state while users are being fetched
                        if (users.length === 0) return "Loading...";

                        const operator = users.find(
                          (u) => u.id == m.operatorId
                        );
                        return operator
                          ? `${operator.first_name} ${operator.last_name}`
                          : "Unassigned";
                      })()}
                    </span> */}

                    <span className="fw-medium">
                      {(() => {
                        if (users.length === 0) return "Loading...";
                        const operator = users.find(
                          (u) => u.id == m.operatorId,
                        );
                        return operator ? operator.name : "Unassigned";
                        {
                          /* Changed */
                        }
                      })()}
                    </span>
                  </div>

                  <div className="d-flex align-items-center gap-2 small mb-2">
                    <Gauge size={16} className="text-muted" />
                    <span className="text-muted">Capacity/hr:</span>
                    <span className="fw-medium">{m.capacityPerHour}</span>
                  </div>
                  <div className="d-flex align-items-center gap-2 small mb-2">
                    <span className="text-muted">Machine Rate:</span>
                    <span className="fw-medium">
                      {(Number(m.hourlyRate) || 0).toFixed(2)} {m.hourlyRateCurrency || "CAD"}/hr
                    </span>
                  </div>

                  <div className="mb-2">
                    <div className="d-flex justify-content-between small">
                      <span className="text-muted">Live Utilization</span>
                      <span className="fw-semibold">
                        {Math.max(0, Math.min(100, Number(m.utilization) || 0))}
                        %
                      </span>
                    </div>

                    <ProgressBar
                      className="machine-utilization-progress"
                      now={Math.max(
                        0,
                        Math.min(100, Number(m.utilization) || 0),
                      )}
                    />
                  </div>

                  {/* <UtilizationSpark data={buildUtilizationSeries(m.id)} /> */}

                  <UtilizationSpark
                    data={buildUtilizationSeries(m.code || m.id)}
                  />

                  <div className="d-flex justify-content-between text-muted small mt-2">
                    <div>Last svc: {m.lastService}</div>
                    <div className="d-flex align-items-center gap-1">
                      <Calendar size={14} /> Next: {m.nextService}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Card className="shadow-sm">
          <Card.Body>
            {tableSelection.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mb-2 p-2 rounded border bg-light">
                <div className="small">{tableSelection.length} selected</div>
                <div className="d-flex gap-2">
                  <Form.Select
                    size="sm"
                    style={{ maxWidth: 180 }}
                    onChange={(e) => {
                      const status = e.target.value;
                      if (!status) return;
                      setMachines((prev) =>
                        prev.map((m) =>
                          tableSelection.includes(m.id) ? { ...m, status } : m,
                        ),
                      );
                    }}
                  >
                    <option value="">Set status…</option>
                    {STATUS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Select
                    size="sm"
                    style={{ maxWidth: 200 }}
                    onChange={(e) => {
                      const op = e.target.value;
                      setMachines((prev) =>
                        prev.map((m) =>
                          tableSelection.includes(m.id)
                            ? { ...m, operatorId: op }
                            : m,
                        ),
                      );
                    }}
                  >
                    <option value="">Assign operator…</option>
                    {/* {OPERATORS.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))} */}

                    {/* {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </option>
                    ))} */}

                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name} {/* Changed */}
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() =>
                      exportCSV(
                        machines.filter((m) => tableSelection.includes(m.id)),
                      )
                    }
                  >
                    <Download size={14} className="me-1" /> Export selected
                  </Button>
                </div>
              </div>
            )}
            <div style={{ maxHeight: 580, overflow: "auto" }}>
              <Table hover responsive>
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>
                      <Form.Check
                        type="checkbox"
                        checked={allChecked}
                        ref={(el) => {
                          if (el) el.indeterminate = someChecked;
                        }}
                        onChange={toggleAll}
                      />
                    </th>
                    <th>{headerSortButton("id", "Code")}</th>
                    <th>{headerSortButton("name", "Name")}</th>
                    <th>{headerSortButton("department", "Dept")}</th>
                    <th>{headerSortButton("type", "Type")}</th>
                    <th>{headerSortButton("heads", "Heads")}</th>
                    <th>
                      {headerSortButton("capacityPerHour", "Capacity/hr")}
                    </th>
                    <th>{headerSortButton("hourlyRate", "Rate/hr")}</th>
                    <th>{headerSortButton("operator", "Operator")}</th>
                    <th>{headerSortButton("status", "Status")}</th>
                    <th>{headerSortButton("utilization", "Utilization")}</th>
                    <th>{headerSortButton("nextService", "Next Service")}</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((m) => (
                    <tr
                      key={m.id}
                      onClick={(e) => {
                        if (e.target.type !== "checkbox") openMachine(m);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={tableSelection.includes(m.id)}
                          onChange={() => toggleOne(m.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="fw-semibold">{m.code}</td>
                      <td>{m.name}</td>
                      {/* <td>
                        {DEPARTMENTS.find((d) => d.id === m.department)?.name}
                      </td> */}
                      <td>{m.department}</td>
                      <td>{m.type || "N/A"}</td>
                      <td>{m.heads}</td>
                      <td>{m.capacityPerHour}</td>
                      <td>
                        {(Number(m.hourlyRate) || 0).toFixed(2)}{" "}
                        {m.hourlyRateCurrency || "CAD"}
                      </td>
                      <td>
                        {/* {OPERATORS.find((o) => o.id === m.operatorId)?.name ||
                          "—"} */}

                        {/* <td>
                          {(() => {
                            const operator = users.find(
                              (u) => u.id == m.operatorId
                            );
                            return operator
                              ? `${operator.first_name} ${operator.last_name}`
                              : "-";
                          })()}
                        </td> */}

                        {/* <td>
                          {(() => {
                            if (users.length === 0) return "Loading...";

                            const operator = users.find(
                              (u) => u.id == m.operatorId
                            );
                            return operator
                              ? `${operator.first_name} ${operator.last_name}`
                              : "-";
                          })()}
                        </td> */}
                        <td>
                          {(() => {
                            if (users.length === 0) return "Loading...";
                            const operator = users.find(
                              (u) => u.id == m.operatorId,
                            );
                            return operator ? operator.name : "-";
                            {
                              /* Changed */
                            }
                          })()}
                        </td>
                      </td>
                      <td>
                        <Badge bg={statusVariant(m.status)}>{m.status}</Badge>
                        {serviceBadge(m.nextService)}
                      </td>
                      <td style={{ width: 160 }}>
                        <div className="d-flex align-items-center gap-2">
                          <div style={{ width: 120 }}>
                            <ProgressBar
                              className="machine-utilization-progress"
                              now={Math.max(
                                0,
                                Math.min(100, Number(m.utilization) || 0),
                              )}
                            />
                          </div>
                          <span className="small fw-semibold">
                            {m.utilization}%
                          </span>
                        </div>
                      </td>
                      <td>{m.nextService}</td>
                      <td>
                        <Button
                          className="me-2"
                          size="sm"
                          variant="outline-secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowEdit(true);
                            setEditMachine(m);
                          }}
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>

                        <Button
                          size="sm"
                          variant="outline-info"
                          onClick={(e) => {
                            e.stopPropagation();
                            openAuditLog(m);
                          }}
                        >
                          <i className="bi bi-clock-history"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}
      {/* Details Offcanvas */}
      <Offcanvas
        show={showDetail}
        onHide={() => setShowDetail(false)}
        placement="end"
        backdrop
      >
        <Offcanvas.Header closeButton className="align-items-start">
          <div className="d-flex flex-column w-100">
            {/* Title Row */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex align-items-center gap-2">
                <Factory size={20} className="text-primary" />
                <h5 className="mb-0">{selected?.name || "Machine"}</h5>
              </div>

              {/* Edit Button */}
              {/* {selected && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setShowEdit(true)}
                >
                  <i className="bi bi-pencil-square me-1"></i> Edit
                </Button>
              )} */}

              {/* Edit Button (when a machine is already selected) */}
              {selected && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setEditMachine(selected); // ✅ this was missing
                    setShowEdit(true);
                  }}
                >
                  <i className="bi bi-pencil-square me-1"></i> Edit
                </Button>
              )}
            </div>

            {/* Status + Service Badges Row */}
            {selected && (
              <div className="d-flex flex-wrap gap-2">
                <Badge bg={statusVariant(selected.status)}>
                  {selected.status}
                </Badge>
                {serviceBadge(selected.nextService)}
              </div>
            )}
          </div>
        </Offcanvas.Header>

        <Offcanvas.Body>
          {selected && (
            <div className="d-flex flex-column gap-3">
              <div className="text-muted small">
                {selected.code} • {selected.department} • Heads {selected.heads}
              </div>

              <Row>
                <Col md={6}>
                  <Card className="mb-3 h-100">
                    <Card.Body>
                      <div className="d-flex justify-content-between small mb-2">
                        <div className="fw-semibold" style={{ fontSize: 22 }}>
                          {selected.utilization}%
                        </div>
                        <div className="text-muted">
                          Capacity/hr {selected.capacityPerHour}
                        </div>
                      </div>
                      <UtilizationSpark
                        data={buildUtilizationSeries(selected.id)}
                      />
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="mb-3 h-100">
                    <Card.Body className="small">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted">Last Service</span>
                        <span>{selected.lastService}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Next Service</span>
                        <span>{selected.nextService}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Machine Rate</span>
                        <span>
                          {(Number(selected.hourlyRate) || 0).toFixed(2)}{" "}
                          {selected.hourlyRateCurrency || "CAD"}/hr
                        </span>
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        {/* <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() =>
                            updateSelected({ status: "Maintenance" })
                          }
                        >
                          <Wrench size={14} className="me-1" />
                          Schedule
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => updateSelected({ status: "Running" })}
                        >
                          <Play size={14} className="me-1" />
                          Start
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => updateSelected({ status: "Idle" })}
                        >
                          <Pause size={14} className="me-1" />
                          Pause
                        </Button> */}

                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={isUpdating}
                          onClick={() =>
                            updateSelected({ status: "Maintenance" })
                          }
                        >
                          <Wrench size={14} className="me-1" />
                          Schedule
                        </Button>

                        <Button
                          size="sm"
                          disabled={isUpdating}
                          onClick={() => updateSelected({ status: "Running" })}
                        >
                          <Play size={14} className="me-1" />
                          Start
                        </Button>

                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={isUpdating}
                          onClick={() => updateSelected({ status: "Idle" })}
                        >
                          <Pause size={14} className="me-1" />
                          Pause
                        </Button>

                        <Form.Check
                          type="switch"
                          id="power"
                          label={selected.powerOn ? "Power ON" : "Power OFF"}
                          checked={selected.powerOn}
                          onChange={(e) =>
                            updateSelected({ powerOn: e.target.checked })
                          }
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* <Card>
                <Card.Header className="py-2">
                  Operator / Assignment
                </Card.Header>
                <Card.Body className="d-flex align-items-center gap-2 flex-wrap">
                  <Form.Select
                    style={{ maxWidth: 220 }}
                    value={selected.operatorId}
                    onChange={(e) =>
                      updateSelected({ operatorId: e.target.value })
                    }
                  >
                    {OPERATORS.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </Form.Select>
                  <div className="text-muted small ms-2">Status:</div>

                  <Form.Select
                    style={{ maxWidth: 160 }}
                    value={selected.status || ""}
                    onChange={(e) => updateSelected({ status: e.target.value })}
                  >
                    {STATUS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </Card.Body>
              </Card> */}

              <Card>
                <Card.Header className="py-2">
                  Operator / Assignment
                </Card.Header>
                <Card.Body>
                  {/* Helper Text */}
                  <div
                    className="text-muted small mb-2"
                    style={{ fontSize: "11px" }}
                  >
                    You can change the operator and status
                  </div>

                  {/* Single Row - Compact Layout */}
                  <div className="row g-3 align-items-center">
                    {/* Enhanced Operator Dropdown */}
                    {/* Operator 50% */}
                    <div className="col-12 col-md-6 d-flex align-items-center gap-2">
                      <div className="text-muted small">Operator:</div>
                      <div
                        style={{
                          position: "relative",

                          // minWidth: 180,

                          flex: "1 1 auto",
                        }}
                        ref={operatorDropdownRef}
                      >
                        {/* Main Input - Shows selected operator or search */}
                        <div style={{ position: "relative" }}>
                          <FormControl
                            size="sm"
                            placeholder={getOperatorDisplayText()}
                            value={operatorSearchQuery}
                            onChange={handleOperatorInputChange}
                            onFocus={() => setShowOperatorDropdown(true)}
                            style={{
                              // width: 180,
                              width: "100%", // responsive width
                              paddingRight: "30px", // Space for dropdown arrow
                              cursor: showOperatorDropdown ? "text" : "pointer",
                            }}
                            readOnly={
                              !showOperatorDropdown && !operatorSearchQuery
                            }
                          />

                          {/* Dropdown Arrow */}
                          <div
                            style={{
                              position: "absolute",
                              right: "8px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              cursor: "pointer",
                              color: "#6c757d",
                              fontSize: "12px",
                              pointerEvents: "none",
                            }}
                          >
                            {showOperatorDropdown ? "▲" : "▼"}
                          </div>

                          {/* Clear Button (when searching) */}
                          {operatorSearchQuery && (
                            <button
                              type="button"
                              style={{
                                position: "absolute",
                                right: "25px", // Adjusted for dropdown arrow
                                top: "50%",
                                transform: "translateY(-50%)",
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                color: "#666",
                                fontSize: "16px",
                                padding: "0",
                                width: "15px",
                                height: "15px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 1,
                              }}
                              onClick={handleOperatorClear}
                            >
                              ×
                            </button>
                          )}
                        </div>

                        {/* Dropdown Menu */}
                        {showOperatorDropdown && (
                          <div
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              right: 0,
                              backgroundColor: "#fff",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                              zIndex: 1001,
                              maxHeight: "150px",
                              overflowY: "auto",
                            }}
                          >
                            {filteredUsers.length === 0 ? (
                              <div
                                style={{
                                  padding: "6px 10px",
                                  color: "#666",
                                  fontSize: "12px",
                                }}
                              >
                                {operatorSearchQuery
                                  ? "No operators found"
                                  : "No operators available"}
                              </div>
                            ) : (
                              <>
                                <div
                                  style={{
                                    padding: "6px 10px",
                                    cursor: "pointer",
                                    fontStyle: "italic",
                                    color: "#666",
                                    fontSize: "12px",
                                  }}
                                  onClick={handleOperatorClear}
                                  onMouseEnter={(e) =>
                                    (e.target.style.backgroundColor = "#f5f5f5")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.target.style.backgroundColor = "#fff")
                                  }
                                >
                                  Unassigned
                                </div>
                                {filteredUsers.map((user) => (
                                  <div
                                    key={user.id}
                                    style={{
                                      padding: "6px 10px",
                                      cursor: "pointer",
                                      fontSize: "12px",
                                      backgroundColor:
                                        selected?.operatorId === user.id
                                          ? "#e3f2fd"
                                          : "#fff",
                                    }}
                                    // onClick={() =>
                                    //   handleOperatorSelect(
                                    //     user.id,
                                    //     user.first_name,
                                    //     user.last_name
                                    //   )
                                    // }

                                    onClick={
                                      () =>
                                        handleOperatorSelect(user.id, user.name) // Changed parameters
                                    }
                                    onMouseEnter={(e) => {
                                      if (selected?.operatorId !== user.id) {
                                        e.target.style.backgroundColor =
                                          "#f5f5f5";
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (selected?.operatorId !== user.id) {
                                        e.target.style.backgroundColor = "#fff";
                                      }
                                    }}
                                  >
                                    <div style={{ fontWeight: "500" }}>
                                      {/* {user.first_name} {user.last_name} */}
                                      {user.name} {/* Changed */}
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status Dropdown - Enhanced with helper styling */}

                    {/* Status 50% */}
                    <div className="col-12 col-md-6 d-flex align-items-center gap-2">
                      <div className="text-muted small">Status:</div>
                      <Form.Select
                        size="sm"
                        // style={{ width: 130 }}
                        style={{ width: "100%" }} // full width inside its column
                        value={selected?.status || ""}
                        onChange={(e) =>
                          updateSelected({ status: e.target.value })
                        }
                      >
                        {STATUS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header className="py-2">
                  Job Queue (drag to reorder)
                </Card.Header>
                <Card.Body>
                  {selected.queue && selected.queue.length ? (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={({ active, over }) => {
                        if (!over || active.id === over.id) return;
                        const oldIndex = selected.queue.findIndex(
                          (i) => i.id === active.id,
                        );
                        const newIndex = selected.queue.findIndex(
                          (i) => i.id === over.id,
                        );
                        updateSelected({
                          queue: arrayMove(selected.queue, oldIndex, newIndex),
                        });
                      }}
                    >
                      <SortableContext
                        items={selected.queue.map((q) => q.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {selected.queue.map((q, idx) => (
                          <SortableQueueRow key={q.id} item={q} index={idx} />
                        ))}
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <div className="text-muted small">No queued jobs.</div>
                  )}

                  {/* Fallback buttons (also visible along with DnD) */}
                  {selected.queue && selected.queue.length ? (
                    <div className="mt-2 d-flex flex-column gap-2">
                      {selected.queue.map((q, idx) => (
                        <QueueRowFallback
                          key={q.id + "-fallback"}
                          item={q}
                          index={idx}
                          total={selected.queue.length}
                          onMove={moveQueue}
                        />
                      ))}
                    </div>
                  ) : null}
                </Card.Body>
              </Card>
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>
      {/* Add Machine Modal */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Machine</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-2">
            <Col xs={12}>
              {/* <Form.Label>Code</Form.Label>
              <Form.Control
                value={newMachine.id}
                isInvalid={touched.id && !newMachine.id}
                onChange={(e) =>
                  setNewMachine({ ...newMachine, id: e.target.value })
                }
                placeholder="e.g. EMB-05"
              />
              <Form.Control.Feedback type="invalid">
                Code is required
              </Form.Control.Feedback> */}

              <Form.Label>Code</Form.Label>
              <Form.Control
                value={newMachine.code}
                isInvalid={touched.code && !newMachine.code}
                onChange={(e) =>
                  setNewMachine({ ...newMachine, code: e.target.value })
                }
                placeholder="e.g. SCRN-B"
              />
              <Form.Control.Feedback type="invalid">
                Code is required
              </Form.Control.Feedback>
            </Col>
            <Col xs={12}>
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={newMachine.name}
                isInvalid={touched.name && !newMachine.name}
                onChange={(e) =>
                  setNewMachine({ ...newMachine, name: e.target.value })
                }
                placeholder="e.g. Tajima 4-Head"
              />
              <Form.Control.Feedback type="invalid">
                Name is required
              </Form.Control.Feedback>
            </Col>
            <Col md={6}>
              {/* <Form.Label>Department</Form.Label>
              <Form.Select
                value={newMachine.department}
                onChange={(e) =>
                  setNewMachine({ ...newMachine, department: e.target.value })
                }
              >
                {DEPARTMENTS.filter((d) => d.id !== "ALL").map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Form.Select> */}

              {/* <Form.Label>Department</Form.Label>
              <Form.Select
                value={newMachine.department_id}
                onChange={(e) =>
                  setNewMachine({
                    ...newMachine,
                    department_id: Number(e.target.value),
                  })
                }
              >
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Form.Select> */}

              <Form.Group>
                <Form.Label>Department</Form.Label>
                <Form.Select
                  required // <-- this makes it required
                  value={newMachine.department_id}
                  isInvalid={touched.code && !newMachine.department_id} // show error if not selected
                  onChange={(e) => {
                    const dept = departments.find(
                      (d) => d.id === parseInt(e.target.value),
                    );
                    if (dept) {
                      setNewMachine((prev) => ({
                        ...prev,
                        department_id: dept.id,
                        type: dept.name,
                      }));
                    }
                  }}
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  Department is required
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Label>Type</Form.Label>
              <Form.Control
                value={newMachine.type}
                onChange={(e) =>
                  setNewMachine({ ...newMachine, type: e.target.value })
                }
              />
            </Col>
            <Col md={6}>
              <Form.Label>Heads</Form.Label>
              <Form.Control
                type="number"
                value={newMachine.heads}
                onChange={(e) =>
                  setNewMachine({
                    ...newMachine,
                    heads: Number(e.target.value),
                  })
                }
              />
            </Col>
            <Col md={6}>
              <Form.Label>Capacity/hr</Form.Label>
              <Form.Control
                type="number"
                value={newMachine.capacityPerHour}
                onChange={(e) =>
                  setNewMachine({
                    ...newMachine,
                    capacityPerHour: Number(e.target.value),
                  })
                }
              />
            </Col>
            <Col md={6}>
              <Form.Label>Machine Hourly Rate</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={newMachine.hourlyRate}
                onChange={(e) =>
                  setNewMachine({
                    ...newMachine,
                    hourlyRate: Number(e.target.value),
                  })
                }
              />
            </Col>
            <Col md={6}>
              <Form.Label>Rate Currency</Form.Label>
              <Form.Control
                value={newMachine.hourlyRateCurrency}
                onChange={(e) =>
                  setNewMachine({
                    ...newMachine,
                    hourlyRateCurrency: e.target.value,
                  })
                }
                placeholder="CAD"
              />
            </Col>
            <Col md={6}>
              <Form.Label>Operator</Form.Label>
              <Form.Select
                value={newMachine.operatorId}
                onChange={(e) =>
                  setNewMachine({ ...newMachine, operatorId: e.target.value })
                }
              >
                <option value="">Unassigned</option>
                {/* {OPERATORS.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))} */}

                {/* {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name}
                  </option>
                ))} */}
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} {/* Changed */}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={newMachine.status}
                onChange={(e) =>
                  setNewMachine({ ...newMachine, status: e.target.value })
                }
              >
                {STATUS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Next Service</Form.Label>
              <Form.Control
                type="date"
                value={newMachine.nextService}
                onChange={(e) =>
                  setNewMachine({ ...newMachine, nextService: e.target.value })
                }
              />
            </Col>
            <Col md={6}>
              <Form.Label>Last Service</Form.Label>
              <Form.Control
                type="date"
                value={newMachine.lastService}
                onChange={(e) =>
                  setNewMachine({ ...newMachine, lastService: e.target.value })
                }
              />
            </Col>
            <Col xs={12}>
              <Form.Check
                type="switch"
                id="power-new"
                label={newMachine.powerOn ? "Power ON" : "Power OFF"}
                checked={newMachine.powerOn}
                onChange={(e) =>
                  setNewMachine({ ...newMachine, powerOn: e.target.checked })
                }
              />
            </Col>
            <Col xs={12}>
              <Form.Label>Initial Utilization %</Form.Label>
              <Form.Control
                type="number"
                value={newMachine.utilization}
                onChange={(e) =>
                  setNewMachine({
                    ...newMachine,
                    utilization: Number(e.target.value),
                  })
                }
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={addMachine}>
            <Plus size={16} className="me-1" />
            Add
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showEdit} onHide={() => setShowEdit(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Machine</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editMachine && ( // ✅ only render if editMachine is set
            (<Row className="g-2">
              <Col xs={12}>
                <Form.Label>Code</Form.Label>
                <Form.Control
                  value={editMachine.code}
                  onChange={(e) =>
                    setEditMachine({ ...editMachine, code: e.target.value })
                  }
                />
              </Col>
              <Col xs={12}>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  value={editMachine.name}
                  onChange={(e) =>
                    setEditMachine({ ...editMachine, name: e.target.value })
                  }
                />
              </Col>
              <Col md={6}>
                <Form.Label>Department</Form.Label>
                {/*  <Form.Select
                  value={editMachine.department_id}
                  onChange={(e) =>
                    setEditMachine({
                      ...editMachine,
                      department_id: e.target.value,
                    })
                  }
                >
                  {DEPARTMENTS.filter((d) => d.id !== "ALL").map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </Form.Select> */}

                <Form.Select
                  value={editMachine.department_id}
                  onChange={(e) => {
                    const dept = departments.find(
                      (d) => d.id === parseInt(e.target.value),
                    );
                    if (dept) {
                      setEditMachine({
                        ...editMachine,
                        department_id: dept.id,
                        department: dept.name,
                        type: dept.name,
                      });
                    }
                  }}
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Type</Form.Label>
                <Form.Control
                  value={editMachine.type || ""}
                  onChange={(e) =>
                    setEditMachine({ ...editMachine, type: e.target.value })
                  }
                  placeholder="Machine type"
                />
              </Col>
              <Col md={6}>
                <Form.Label>Heads</Form.Label>
                <Form.Control
                  type="number"
                  value={editMachine.heads}
                  onChange={(e) =>
                    setEditMachine({
                      ...editMachine,
                      heads: Number(e.target.value),
                    })
                  }
                />
              </Col>
              <Col md={6}>
                <Form.Label>Capacity/hr</Form.Label>
                <Form.Control
                  type="number"
                  value={editMachine.capacityPerHour}
                  onChange={(e) =>
                    setEditMachine({
                      ...editMachine,
                      capacityPerHour: Number(e.target.value),
                    })
                  }
                />
              </Col>
              <Col md={6}>
                <Form.Label>Machine Hourly Rate</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={editMachine.hourlyRate ?? 0}
                  onChange={(e) =>
                    setEditMachine({
                      ...editMachine,
                      hourlyRate: Number(e.target.value),
                    })
                  }
                />
              </Col>
              <Col md={6}>
                <Form.Label>Rate Currency</Form.Label>
                <Form.Control
                  value={editMachine.hourlyRateCurrency || "CAD"}
                  onChange={(e) =>
                    setEditMachine({
                      ...editMachine,
                      hourlyRateCurrency: e.target.value,
                    })
                  }
                  placeholder="CAD"
                />
              </Col>
              <Col md={6}>
                <Form.Label>Next Service</Form.Label>
                <Form.Control
                  type="date"
                  value={editMachine.nextService || ""} // fallback if empty
                  onChange={(e) =>
                    setEditMachine({
                      ...editMachine,
                      nextService: e.target.value,
                    })
                  }
                />
              </Col>
              <Col md={6}>
                <Form.Label>Last Service</Form.Label>
                <Form.Control
                  type="date"
                  value={editMachine.lastService || ""}
                  onChange={(e) =>
                    setEditMachine({
                      ...editMachine,
                      lastService: e.target.value,
                    })
                  }
                />
              </Col>
              {/* You can add other fields like Status, Operator, Service Dates etc. */}
            </Row>)
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEdit(false)}>
            Cancel
          </Button>
          {/* <Button
            variant="primary"
            onClick={() => {
              updateSelected(editMachine); // temp dummy update
              setShowEdit(false);
            }}
          >
            Save Changes
          </Button> */}

          {/* <Button
            variant="primary"
            onClick={async () => {
              try {
                // const updated = await patchMachine(editMachine.id, editMachine);

                const updated = await patchMachine(
                  editMachine.machineId || editMachine.id,
                  editMachine
                );
                console.log(
                  "PATCH URL ID:",
                  editMachine.id || editMachine.machineId
                );
                console.log("Payload:", editMachine);

                // Update local state with API response
                updateSelected(updated);
                setShowEdit(false);
              } catch (err) {
                alert("Update failed: " + err.message);
              }
            }}
          >
            Save Changes
          </Button> */}

          <Button
            variant="primary"
            onClick={async () => {
              try {
                // Always use the DB id for the URL
                const updated = await patchMachine(editMachine.id, editMachine);

                console.log("PATCH URL ID:", editMachine.id);
                console.log("Payload:", editMachine);

                // Update local state with API response
                updateSelected(updated);
                setShowEdit(false);
              } catch (err) {
                alert("Update failed: " + err.message);
              }
            }}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Audit Log Modal */}
      <Modal
        show={showAuditLog}
        onHide={() => setShowAuditLog(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Audit Log</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Action</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map((log, i) => (
                <tr key={i}>
                  <td>{new Date(log.date).toLocaleString()}</td>
                  <td>{log.user}</td>
                  <td>{log.action}</td>
                  <td>{log.details}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>
    </Container>
  );
}
